import { createTheme } from "@material-ui/core/styles";

// Variables
let variables = {
  border: {
    primary: "rgb(213, 205, 205)",
  },
  text: {
    primary: "#0a0a29",
    secondary: "#0028fa",
  },
};

// default theme
const theme = createTheme();

export const customTheme = createTheme({
  mainContent: {
    width: "100%",
    maxWidth: theme.breakpoints.values.lg,
    padding: "15px 0px",
  },
  mainContentMD: {
    width: "100%",
    maxWidth: "1020px",
    padding: "15px 45px",
    margin: "0 auto",
  },
  mainContentLG: {
    width: "100%",
    maxWidth: "1240px",
    padding: "15px 45px",
    margin: "0 auto",
  },
  overrides: {
    // CssBaseline paints body #fafafa by default and its JSS lands after
    // index.css, so it has to be overridden here rather than in the stylesheet.
    MuiCssBaseline: {
      "@global": {
        body: { backgroundColor: "var(--cc-bg, #ffffff)" },
      },
    },
    // Loading skeletons in the brand's blue tint, like the design-system bars
    // (tokens.css --cc-skeleton). MUI's JSS lands after the stylesheets, so the
    // colour has to be set here.
    MuiSkeleton: {
      root: { backgroundColor: "var(--cc-skeleton)" },
      wave: {
        "&::after": {
          background:
            "linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.6), transparent)",
        },
      },
    },
    MuiFormHelperText: {
      root: { fontFamily: ['"Source Sans Pro"', "sans-serif"].join(", ") },
    },
    MuiButton: {
      root: {
        textTransform: "none",
      },
      contained: {
        boxShadow: "none",
        "&:hover, &:active, &:focus": {
          boxShadow: "none",
        },
      },
    },
    MuiDivider: {
      vertical: {
        alignSelf: "stretch",
        height: "auto",
        margin: "0 0.5rem",
      },
    },
    MuiTableHead: {
      root: {
        backgroundColor: "#f1f5f9",
        textTransform: "uppercase",
        boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
        borderBottom: "1px solid #e5e7eb",
      },
    },
    MuiTableContainer: {
      root: { overflowX: "unset" },
    },
    MuiListItem: {
      root: {
        "&$selected": {
          backgroundColor: "#f7f7f8",
          fontWeight: 700,
          borderRadius: "15px 0px 0px 15px",
        },
      },
    },
    MuiCheckbox: {
      root: {
        color: "#2563eb",
        width: "30px",
        height: "30px",
      },
    },
  },
  palette: {
    primary: {
      light: "#333f4c",
      main: "#2563eb",
      dark: "#6d7b8a",
      contrastText: "#fff",
    },
    secondary: {
      light: "#972f21",
      main: "#181818",
      dark: "#e06959",
      contrastText: "#fff",
    },
    danger: {
      main: "#ab003c",
    },
    red: {
      main: "#ab003c",
    },
    text: {
      primary: "#0a0a29",
    },
    disabled: {
      main: "#A4A4A4",
    },
  },
  typography: {
    h1: {
      fontFamily: ['"Mulish"', "sans-serif"].join(", "),
      fontSize: "18px",
      lineHeight: "26px",
      margin: 0,
      padding: 0,
    },
    h2: {
      fontFamily: ['"Mulish"', "sans-serif"].join(", "),
      fontSize: "16px",
      lineHeight: "19px",
      margin: 0,
      padding: 0,
    },
    h3: {
      fontFamily: ['"Mulish"', "sans-serif"].join(", "),
      fontSize: "14px",
      lineHeight: "19px",
      margin: 0,
      padding: 0,
    },
    hlg: {
      fontFamily: ['"Mulish"', "sans-serif"].join(", "),
      fontSize: "24px",
      lineHeight: "32px",
      margin: 0,
      padding: 0,
    },
    hxl: {
      fontFamily: ['"Mulish"', "sans-serif"].join(", "),
      fontSize: "32px",
      lineHeight: "42px",
      margin: 0,
      padding: 0,
    },
    fontFamily: ['"Mulish"', "sans-serif"].join(", "),
    body1: {
      fontFamily: ['"Mulish"', "sans-serif"].join(", "),
      fontSize: "14px",
      lineHeight: "19px",
      fontWeight: 400,
      margin: 0,
      padding: 0,
      color: variables.text.primary,
    },
    body2: {
      fontFamily: ['"Mulish"', "sans-serif"].join(", "),
      fontSize: "13px",
      lineHeight: "18px",
      fontWeight: 400,
      margin: 0,
      padding: 0,
      color: variables.text.primary,
    },
    body3: {
      fontFamily: ['"Mulish"', "sans-serif"].join(", "),
      fontSize: "11px",
      lineHeight: "16px",
      fontWeight: 400,
      margin: 0,
      padding: 0,
      color: variables.text.primary,
    },
    secondaryFontFamily: ['"Source Code Pro"', "monospace"].join(", "),
    caption: {
      fontFamily: ['"Source Code Pro"', "monospace"].join(", "),
      fontSize: "0.85rem",
      margin: 0,
      padding: 0,
    },
    color: "#495B6D",
    bold: {
      fontWeight: 800,
    },
    semiBold: {
      fontWeight: 700,
    },
    breadCrumb: {
      fontFamily: ['"Mulish"', "sans-serif"].join(", "),
      fontSize: "14px",
      lineHeight: "22px",
      fontWeight: 400,
      margin: 0,
      padding: 0,
    },
  },
  background: {
    primary: "#F7F8FA",
  },
  borderBottom: {
    borderBottom: `1px solid ${variables.border.primary}`,
  },
  borderTop: {
    borderTop: `1px solid ${variables.border.primary}`,
  },
  borderRight: {
    borderRight: `1px solid ${variables.border.primary}`,
  },
  borderLeft: {
    borderLeft: `1px solid ${variables.border.primary}`,
  },
  border: {
    border: `1px solid ${variables.border.primary}`,
  },
  primaryColor: {
    color: variables.text.primary,
  },
  secondaryColor: {
    color: variables.text.secondary,
  },
  alignRight: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
  },
});
